import "ag-grid-enterprise";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { calculateEarthing } from "@multiconsult-group/multiplacer-core";
import { MultiViewer } from "@multiconsult-group/multiplacer-ui";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, RowClickedEvent, ValueFormatterParams } from "ag-grid-enterprise";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { openapi } from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useProjectId } from "../../hooks/useProjectId";
import {
  setProjectViewerSelectedEarthingApiRequestId,
  setProjectViewerSelectedEarthingApiRequestLiveError,
  setProjectViewerSelectedEarthingApiRequestLiveResult,
} from "../projectViewerSlice";
import {
  earthingApiRequestsQueryArg,
  type IEarthingApiRequestRow,
  selectEarthingApiRequestRows,
  selectEarthingApiRequestsHasMore,
  selectSelectedEarthingApiRequestHasLiveResult,
  selectSelectedEarthingApiRequestInput,
  selectSelectedEarthingApiRequestInputPolygons,
  selectSelectedEarthingApiRequestLiveError,
  selectSelectedEarthingApiRequestLiveSummary,
  selectSelectedEarthingApiRequestRow,
  selectSelectedEarthingApiRequestViewerLimitSummary,
  selectSelectedEarthingApiRequestViewerPaths,
  selectSelectedEarthingApiRequestZoomExtentKey,
} from "./earthingApiRequestsSelectors";

const defaultColDef: ColDef<IEarthingApiRequestRow> = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 120,
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date);
};

const formatLogPreview = (row: IEarthingApiRequestRow): string => {
  const { raw } = row;
  const input = raw.request.input;
  const output = raw.response.output;
  const firstFoundation = input?.apparatusFoundations[0];

  return JSON.stringify(
    {
      _id: raw._id,
      route: raw.route,
      source: raw.source,
      createdAt: raw.createdAt,
      startedAt: raw.startedAt,
      finishedAt: raw.finishedAt,
      durationMs: raw.durationMs,
      status: raw.status,
      request: {
        requestId: raw.request.requestId,
        ipHash: raw.request.ipHash,
        userAgent: raw.request.userAgent,
        client: raw.request.client,
        inputSummary: input
          ? {
              apparatusFoundationCount: input.apparatusFoundations.length,
              firstApparatusFoundation: firstFoundation
                ? {
                    id: firstFoundation.id,
                    pointCount: firstFoundation.points.length,
                    firstPoints: firstFoundation.points.slice(0, 5),
                  }
                : null,
            }
          : null,
      },
      response: {
        outputSummary: output
          ? {
              systemCode: output.systemCode,
              floorName: output.floorName,
              nodeCount: output.nodes.length,
              edgeCount: output.edges.length,
              firstNode: output.nodes[0],
              firstEdge: output.edges[0],
            }
          : null,
        error: raw.response.error,
      },
      meta: raw.meta,
    },
    null,
    2
  );
};

export const EarthingApiRequestsPage: React.FC = () => {
  const projectNo = useProjectId();
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectEarthingApiRequestRows);
  const selectedRow = useAppSelector(selectSelectedEarthingApiRequestRow);
  const selectedInput = useAppSelector(selectSelectedEarthingApiRequestInput);
  const selectedInputPolygons = useAppSelector(selectSelectedEarthingApiRequestInputPolygons);
  const selectedViewerPaths = useAppSelector(selectSelectedEarthingApiRequestViewerPaths);
  const selectedLiveError = useAppSelector(selectSelectedEarthingApiRequestLiveError);
  const selectedLiveSummary = useAppSelector(selectSelectedEarthingApiRequestLiveSummary);
  const hasLiveResult = useAppSelector(selectSelectedEarthingApiRequestHasLiveResult);
  const selectedZoomExtentKey = useAppSelector(selectSelectedEarthingApiRequestZoomExtentKey);
  const selectedViewerLimitSummary = useAppSelector(
    selectSelectedEarthingApiRequestViewerLimitSummary
  );
  const hasMore = useAppSelector(selectEarthingApiRequestsHasMore);
  const [isRunningLive, setIsRunningLive] = useState(false);
  const [isCopyingJson, setIsCopyingJson] = useState(false);
  const selectedLogPreview = useMemo(
    () => (selectedRow == null ? "" : formatLogPreview(selectedRow)),
    [selectedRow]
  );
  const { isLoading, isFetching, isError, refetch } =
    openapi.useGetEarthingRunsQuery(earthingApiRequestsQueryArg);

  const columnDefs = useMemo<ColDef<IEarthingApiRequestRow>[]>(
    () => [
      {
        headerName: "Tid",
        field: "createdAt",
        minWidth: 190,
        valueFormatter: (params: ValueFormatterParams<IEarthingApiRequestRow, string>) =>
          formatDateTime(params.value),
      },
      {
        headerName: "Status",
        field: "status",
        width: 120,
      },
      {
        headerName: "Tid (ms)",
        field: "durationMs",
        width: 110,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Fundament",
        field: "apparatusFoundationCount",
        width: 130,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Noder",
        field: "nodeCount",
        width: 110,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Kanter",
        field: "edgeCount",
        width: 110,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Klient",
        field: "clientName",
        minWidth: 150,
      },
      {
        headerName: "Klientversjon",
        field: "clientVersion",
        minWidth: 150,
      },
      {
        headerName: "Runtime",
        field: "runtimeName",
        minWidth: 140,
      },
      {
        headerName: "Runtimeversjon",
        field: "runtimeVersion",
        minWidth: 150,
      },
      {
        headerName: "Request ID",
        field: "requestId",
        minWidth: 240,
      },
      {
        headerName: "Feil",
        field: "errorMessage",
        minWidth: 260,
        flex: 1,
      },
    ],
    []
  );

  const handleRowClicked = (event: RowClickedEvent<IEarthingApiRequestRow>) => {
    dispatch(setProjectViewerSelectedEarthingApiRequestId(event.data?.id ?? null));
  };

  const handleRunLiveEarthing = () => {
    if (selectedInput == null) return;

    try {
      setIsRunningLive(true);
      const result = calculateEarthing(selectedInput);
      dispatch(setProjectViewerSelectedEarthingApiRequestLiveResult(result));
    } catch (err) {
      console.error("[Jordning API-kall] live calculateEarthing failed", err);
      dispatch(
        setProjectViewerSelectedEarthingApiRequestLiveError(
          err instanceof Error ? err.message : String(err)
        )
      );
    } finally {
      setIsRunningLive(false);
    }
  };

  const handleCopyFullJson = async () => {
    if (selectedRow == null || isCopyingJson) return;

    try {
      setIsCopyingJson(true);
      await navigator.clipboard.writeText(JSON.stringify(selectedRow.raw));
    } finally {
      setIsCopyingJson(false);
    }
  };

  return (
    <Box h="100%" w="100%" p="md" style={{ overflow: "hidden" }}>
      <Stack h="100%" gap="md" style={{ minHeight: 0 }}>
        <Group justify="space-between" align="center" style={{ flexShrink: 0 }}>
          <Box>
            <Title order={3}>Jordning API-kall</Title>
            <Text size="sm" c="dimmed">
              Siste innkomne kall
            </Text>
          </Box>

          <Group gap="xs">
            {(isLoading || isFetching) && <Loader size="sm" />}
            <Badge variant="light">{rows.length}/100</Badge>
            {hasMore && <Badge color="yellow">Flere finnes</Badge>}
            <Button
              size="xs"
              variant="filled"
              disabled={selectedInput == null}
              loading={isRunningLive}
              onClick={handleRunLiveEarthing}
            >
              Kjør live
            </Button>
            <Button size="xs" variant="light" onClick={() => refetch()}>
              Oppdater
            </Button>
            <Button size="xs" variant="subtle" color="gray" component={Link} to={`/${projectNo}/viewer`}>
              Til viewer
            </Button>
          </Group>
        </Group>

        {isError ? (
          <Alert color="red" title="Kunne ikke hente API-kall">
            Prøv igjen om en stund.
          </Alert>
        ) : (
          <Group
            align="stretch"
            gap="md"
            wrap="nowrap"
            style={{ flex: 1, minHeight: 0, minWidth: 0 }}
          >
            <Box
              className="ag-theme-quartz"
              style={{ flex: "0 0 48%", minHeight: 0, minWidth: 420 }}
            >
              <AgGridReact<IEarthingApiRequestRow>
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                loading={isLoading || isFetching}
                animateRows
                rowSelection="single"
                onRowClicked={handleRowClicked}
                getRowId={(params) => params.data.id}
                suppressScrollOnNewData
                overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen API-kall funnet</span>"
              />
            </Box>

            <Stack gap="md" style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
              <Box
                style={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  position: "relative",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                {selectedRow == null ? (
                  <Group h="100%" justify="center" align="center">
                    <Text size="sm" c="dimmed">
                      Velg et API-kall for å vise innholdet i viewer.
                    </Text>
                  </Group>
                ) : (
                  <>
                    <Group
                      gap="xs"
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 2,
                        padding: "4px 6px",
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: 4,
                        background: "var(--mantine-color-white)",
                        boxShadow: "var(--mantine-shadow-xs)",
                      }}
                    >
                      <Badge
                        color="red"
                        variant="light"
                        leftSection={
                          <Box
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: "#c92a2a",
                            }}
                          />
                        }
                      >
                        Inndata
                      </Badge>
                      <Badge
                        color="grape"
                        variant="light"
                        leftSection={
                          <Box
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: "#9c36b5",
                            }}
                          />
                        }
                      >
                        Utdata
                      </Badge>
                      <Badge
                        color="cyan"
                        variant={hasLiveResult ? "light" : "outline"}
                        leftSection={
                          <Box
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: "#0c8599",
                            }}
                          />
                        }
                      >
                        Live
                      </Badge>
                    </Group>
                    {selectedLiveError != null && (
                      <Alert
                        color="red"
                        title="Live-beregning feilet"
                        style={{
                          position: "absolute",
                          top: 52,
                          left: 8,
                          right: 8,
                          zIndex: 2,
                          boxShadow: "var(--mantine-shadow-xs)",
                        }}
                      >
                        {selectedLiveError}
                      </Alert>
                    )}
                    {selectedLiveSummary != null && (
                      <Alert
                        color="green"
                        title="Live-beregning kjørt"
                        style={{
                          position: "absolute",
                          top: 52,
                          left: 8,
                          right: 8,
                          zIndex: 2,
                          boxShadow: "var(--mantine-shadow-xs)",
                        }}
                      >
                        {selectedLiveSummary}
                      </Alert>
                    )}
                    {selectedViewerLimitSummary != null && (
                      <Alert
                        color="yellow"
                        title="Viewer-visning begrenset"
                        style={{
                          position: "absolute",
                          bottom: 8,
                          left: 8,
                          right: 8,
                          zIndex: 2,
                          boxShadow: "var(--mantine-shadow-xs)",
                        }}
                      >
                        {selectedViewerLimitSummary}
                      </Alert>
                    )}
                    <MultiViewer
                      twoDOnly
                      twoD={{
                        polygons: selectedInputPolygons,
                        paths: selectedViewerPaths,
                        zoomExtentKey: selectedZoomExtentKey,
                        flip2dCoordinate: true,
                        useLeftClickToPan: true,
                      }}
                      threeD={{
                        fragFiles: [],
                        visibleMcbimFileNames: [],
                      }}
                    />
                  </>
                )}
              </Box>

              <Box
                p="md"
                style={{
                  flex: "0 0 260px",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: 4,
                  background: "var(--mantine-color-gray-0)",
                }}
              >
                <Group justify="space-between" align="center" mb="xs" style={{ flexShrink: 0 }}>
                  <Text fw={600} size="sm">
                    Innhold
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    disabled={selectedRow == null}
                    loading={isCopyingJson}
                    onClick={handleCopyFullJson}
                  >
                    Kopier full JSON
                  </Button>
                </Group>

                <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
                  {selectedRow == null ? (
                    <Text size="sm" c="dimmed">
                      Velg en rad for å vise loggdokumentet.
                    </Text>
                  ) : (
                    <pre
                      style={{
                        margin: 0,
                        fontSize: 12,
                        lineHeight: 1.45,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedLogPreview}
                    </pre>
                  )}
                </ScrollArea>
              </Box>
            </Stack>
          </Group>
        )}
      </Stack>
    </Box>
  );
};
